/*******************************************************
* Design Tokens (the design rails)
*
* The closed set of values the UI is built from, in both looks (GH #278). Every value
* here was collapsed from what the redesigned pieces already use, so building
* on them matches the DFR, claim forms, Super Review New and User Management New.
* If a design needs a value that isn't here, add it here first (and to
* DESIGN.md) instead of writing a one-off literal in `sx`.
*
* designRails.test.ts fails when any file adds literals off these scales.
*
* Key Features:
* - type: font size steps and weights (prefer Typography variants over raw sizes)
* - space: 4px grid in theme.spacing units, plus semantic layout spacing
* - radius / size / border: corner, control-height and stroke scales
* - tint / tinted(): the alpha levels for hover, selected, tag and ring fills
* - motion: durations and easings
*
* Usage Example:
* ```tsx
* import { tokens, tinted } from '@/src/theme/tokens';
*
* <Box sx={{ p: tokens.space.panel, borderRadius: tokens.radius.md, height: tokens.size.field }} />
* <Box sx={theme => ({ bgcolor: tinted(theme.palette.primary.main, 'fill') })} />
* <Typography variant="micro">Remaining</Typography>
* ```
********************************************************/

//########## IMPORTS ##########
//#region IMPORTS
import { alpha, type TypographyVariantsOptions } from '@mui/material/styles';
//#endregion


//########## CONSTANTS & Global Variables ##########
//#region CONSTANTS
/**
 * Font sizes in px. Text should use a Typography variant (see typeScale);
 * raw sizes are for icons and the rare custom element.
 */
const fontSize = {
    micro: 10,      // stat labels, tiny uppercase captions
    tag: 11,        // badges, tags, overline section labels
    caption: 12,    // counts, metadata, timestamps, helper text
    small: 13,      // dense secondary text, grid headers
    body: 14,       // the workhorse (body2)
    large: 16,      // body1, CTA labels, small icons
    title: 18,      // panel and sheet titles (h6)
    page: 20,       // page titles (h5)
    hero: 24,       // phone-first headings (h4)
    display: 32,    // one hero number per screen (h3)
} as const;

/** Icon sizes in px (fontSize on an SvgIcon) */
const iconSize = {
    xs: 12,
    sm: 14,
    md: 16,
    lg: 18,
    xl: 20,
    xxl: 24,
    empty: 48,      // empty-state icon
    hero: 64,       // phone empty-state icon
} as const;

const fontWeight = {
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    /** Phone-first DFR titles and hero numbers only */
    heavy: 800,
} as const;

/** Font stacks; text uses the theme's body font, so only mono is spelled out */
const fontFamily = {
    /** IDs, UIDs and code-like values only */
    mono: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
} as const;

const letterSpacing = {
    normal: 0,
    /** Uppercase labels (micro, overline) */
    caps: '0.06em',
} as const;

const lineHeight = {
    tight: 1.2,
    snug: 1.4,
    normal: 1.5,
} as const;

/**
 * Spacing in theme.spacing units (1 = 8px). The grid is 4px; `nudge` (2px) is
 * only for optical alignment, never for layout gaps.
 */
const space = {
    nudge: 0.25,    // 2px
    xxs: 0.5,       // 4px  icon-to-label, chip gaps
    xs: 1,          // 8px  default gap
    sm: 1.5,        // 12px bar padding, row padding
    md: 2,          // 16px panel padding, page gutter
    lg: 3,          // 24px between sections
    xl: 4,          // 32px
    xxl: 6,         // 48px empty-state breathing room

    // Semantic
    gutter: 2,      // page side padding
    panel: 2,       // padding inside an outlined panel
    barX: 1.5,      // header / action / footer bar
    barY: 1,
    rowX: 1.5,      // list rows
    rowY: 0.5,
    gap: 1,         // default flex/grid gap
    section: 3,     // between sections in a panel
    pageTop: 1,     // above a page's title
    pageBottom: 2,  // below a page's content
    panelDense: 1.5, // inside a compact panel: detail panels, form groups
    buttonGap: 1,   // between buttons in a row
} as const;

/** Every spacing value the rails accept (theme.spacing units, either sign) */
const SPACE_SCALE = [0, 0.25, 0.5, 1, 1.5, 2, 2.5, 3, 4, 5, 6, 8] as const;

/** Corner radii as px strings, so they're not multiplied by theme.shape */
const radius = {
    none: '0px',
    xs: '2px',      // swatches and legend keys 12px or smaller, where 4px would read as a dot
    sm: '4px',      // panels (Paper variant="outlined"), buttons, tags, grid cells
    md: '8px',      // inputs, search fields, nav rows, cards inside a panel
    lg: '12px',     // hero cards, claim-form surfaces
    sheet: '16px',  // bottom sheet top corners
    pill: '999px',  // pills, count badges, CTA pill
    round: '50%',   // dots, avatars
} as const;

/** Control and row heights in px */
const size = {
    tag: 20,        // small chip, tag, avatar in a row
    compact: 28,    // compact button, section header row
    control: 32,    // small button, crumb pill, nav child row, footer button
    field: 36,      // search field, grid row
    bar: 44,        // bars and the minimum touch target
    cta: 48,        // deciding action on phone (Button size="large")
} as const;

/**
 * Button sizes, applied in theme.ts for both looks. Heights are minimums so a label
 * that wraps still fits. Padding is in theme.spacing units; text buttons sit tighter.
 */
const button = {
    compact: { height: size.compact, paddingY: 0, paddingX: space.sm, textPaddingX: space.xs, fontSize: fontSize.caption },
    small: { height: size.control, paddingY: space.xxs, paddingX: space.sm, textPaddingX: space.xs, fontSize: fontSize.small },
    medium: { height: size.field, paddingY: space.xxs, paddingX: space.md, textPaddingX: space.xs, fontSize: fontSize.body },
    large: { height: size.cta, paddingY: space.xs, paddingX: space.lg, textPaddingX: space.md, fontSize: fontSize.large },
} as const;

/** Fixed layout widths in px */
const width = {
    detailLabel: 112,   // label column of a DetailRow
    list: 260,          // list pane of a list + detail layout
    sidePanel: { halfScreen: 360, fullScreen: 400 },  // detail panel beside a grid
    dfrColumn: 720,     // every DFR step (home, form type, forms), so the header doesn't jump between them
    statNumber: 56,     // StatStrip cell clamped to a short number like 99.99 (DFR Size), padding included
} as const;

const border = {
    hairline: 1,    // panels, separators (with palette.divider)
    ring: 1.5,      // tinted ring around status discs and avatars
    focus: 2,       // focus-visible outline
} as const;

/** Alpha levels for tinting a palette color (use with tinted()) */
const tint = {
    hover: 0.04,    // idle row hover
    fill: 0.08,     // selected row, tonal control fill
    fillHover: 0.12, // hover on a selected or tonal control
    tag: 0.16,      // badge / tag / icon disc fill
    border: 0.28,   // tonal control border, status ring
    muted: 0.4,     // pending / de-emphasized status
} as const;

/** Durations in ms; easings mirror theme.transitions.easing */
const motion = {
    fast: 150,      // hover color, fades, icon rotate
    base: 200,      // closing, loading indicators
    enter: 280,     // drawers and panels opening
    emphasis: 400,  // progress bars filling
    easing: {
        standard: 'cubic-bezier(0.4, 0, 0.2, 1)',
        enter: 'cubic-bezier(0.0, 0, 0.2, 1)',
        exit: 'cubic-bezier(0.4, 0, 1, 1)',
    },
} as const;

/**
 * Status colors (success / warning / info) per mode. Fixed across tenant
 * themes: green and amber mean the same thing in every PG (error already
 * comes from the theme scheme, which is the same red in every tenant).
 * Same contract as the rest of the palette:
 * - main: the contained fill; text-grade (>= 4.5:1 on page and paper) for
 *   success and info. Warning's main is a fill only (see FILL_ONLY_STATUS).
 * - light: the container fill (pale in light mode, deep in dark mode)
 * - dark: text and icons on that container
 * - text: bare text and thin icons on the page (readable; for warning, a
 *   darker cut of Site Amber, since amber itself only works as a fill)
 */
const status = {
    light: {
        success: { main: '#26722A', light: '#C8E6C9', dark: '#0B3D10', contrastText: '#FFFFFF', text: '#26722A' },
        // Site Amber is a fill color: no amber reaches 4.5:1 as text on a light page.
        // Amber text goes on the container (light bg + dark text), never bare on the page.
        warning: { main: '#FFA000', light: '#FFECB3', dark: '#2A1A00', contrastText: '#1A1B20', text: '#945A00' },
        info: { main: '#1F5FA6', light: '#D3E4FF', dark: '#001C3A', contrastText: '#FFFFFF', text: '#1F5FA6' },
    },
    dark: {
        success: { main: '#81C784', light: '#1E4D22', dark: '#C8E6C9', contrastText: '#0B2E0F', text: '#81C784' },
        warning: { main: '#FFB74D', light: '#663E00', dark: '#FFE08A', contrastText: '#2A1A00', text: '#FFB74D' },
        info: { main: '#9CCAFF', light: '#0B4A7E', dark: '#D3E4FF', contrastText: '#00315C', text: '#9CCAFF' },
    },
} as const;

/** Status colors whose main is a fill, not a text color, in light mode */
const FILL_ONLY_STATUS = ['warning'] as const;

const tokens = {
    status,
    fontSize,
    iconSize,
    fontWeight,
    fontFamily,
    letterSpacing,
    lineHeight,
    space,
    radius,
    size,
    button,
    width,
    border,
    tint,
    motion,
} as const;
//#endregion


/** Typography variants the rails add to MUI's set (registered in both looks) */
const customTypeVariants = {
    micro: {
        fontSize: `${fontSize.micro / 16}rem`,
        fontWeight: fontWeight.semibold,
        letterSpacing: letterSpacing.caps,
        lineHeight: lineHeight.snug,
        textTransform: 'uppercase',
    },
    tag: {
        fontSize: `${fontSize.tag / 16}rem`,
        fontWeight: fontWeight.bold,
        lineHeight: lineHeight.snug,
        fontVariantNumeric: 'tabular-nums',
    },
    bodySmall: {
        fontSize: `${fontSize.small / 16}rem`,
        fontWeight: fontWeight.regular,
        lineHeight: lineHeight.normal,
        fontVariantNumeric: 'tabular-nums',
    },
} satisfies TypographyVariantsOptions;
//#endregion


//########## FUNCTIONS ##########
//#region FUNCTIONS
/** A palette color at one of the tint levels, e.g. tinted(theme.palette.primary.main, 'fill') */
const tinted = (color: string, level: keyof typeof tint): string => alpha(color, tint[level]);

/**
 * The tenant's color for filled surfaces, with its label color. A theme built outside
 * getDesignTokens (MUI's default, when nothing provides one) has no brand, so it falls back to primary.
 */
const brandOf = (theme: { palette: {
    primary: { main: string; contrastText: string };
    brand?: { main: string; contrastText: string };
} }) => theme.palette.brand ?? theme.palette.primary;

/** px number to rem string on the 16px root */
const rem = (px: number): string => `${px / 16}rem`;
//#endregion


//########## EXPORTS ##########
//#region EXPORTS
export {
    tokens,
    tinted,
    rem,
    brandOf,
    customTypeVariants,
    FILL_ONLY_STATUS,
    SPACE_SCALE
}
//#endregion
